import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Box,
  IconButton,
  Avatar,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Save as SaveIcon, Person as PersonIcon } from '@material-ui/icons';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { FeedbackResponse } from '@backstage-community/plugin-entity-feedback-common';
import { discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

// Inline types - only optimization
interface DisplayFeedbackResponse extends Omit<FeedbackResponse, 'consent' | 'entityRef'> {
  id?: string;
  status?: 'pending' | 'in_progress' | 'resolved';
  contactPreference: 'Yes' | 'No' | 'Not specified';
  originalUserRef?: string; // Store original userRef for deletion
  featureType?: string; // Feature type from feedback
  link?: string; // Link/path where feedback was submitted
  resolvedBy?: string; // Username who resolved the feedback
  resolvedAt?: string; // Timestamp when resolved
}

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 740,
    tableLayout: 'fixed',
  },
  statusSelect: {
    minWidth: 100,
    maxWidth: 140,
  },
  chipPending: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  chipInProgress: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  chipResolved: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  userAvatar: {
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(3),
    height: theme.spacing(3),
    fontSize: '0.75rem',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  userColumn: {
    width: '140px',
  },
  contactColumn: {
    width: '100px',
  },
  featureTypeColumn: {
    width: '100px',
  },
  linkColumn: {
    width: '150px',
    wordBreak: 'break-word',
  },
  commentColumn: {
    width: '300px',
    wordBreak: 'break-word',
    maxWidth: '300px',
  },
  statusColumn: {
    width: '200px',
  },
}));

export const CustomEntityFeedbackResponseContent = () => {
  const classes = useStyles();
  const [responses, setResponses] = useState<DisplayFeedbackResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusChanges, setStatusChanges] = useState<{ [key: string]: string }>({});
  const [statusFilter, setStatusFilter] = useState<string[]>(['pending', 'in_progress']); // Default filter
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingResolveId, setPendingResolveId] = useState<string | null>(null);

  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;

  useEffect(() => {
    const loadResponses = async () => {
      try {

        // Always use our direct database API since the original has Azure AD filtering issues
        try {
          // Use Backstage discovery API to get proper backend URL
          const baseUrl = await discoveryApi.getBaseUrl('feedback-direct');
          const fullUrl = `${baseUrl}/unfiltered-responses/${encodeURIComponent(entityRef)}`;

          // Get authentication token for the API request
          const { token } = await identityApi.getCredentials();

          const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });


          if (response.ok) {
            const responseText = await response.text();

            try {
              const feedbackResponses = JSON.parse(responseText);

              const displayResponses: DisplayFeedbackResponse[] = feedbackResponses.map((response: any, index: number) => {
                // Extract feature type, link, and isAnonymous from comments if available
                let featureType = 'Not specified';
                let link = '-';
                let isAnonymous = false;
                try {
                  if (response.comments) {
                    const commentsObj = typeof response.comments === 'string'
                      ? JSON.parse(response.comments)
                      : response.comments;
                    featureType = commentsObj.featureType || 'Not specified';
                    isAnonymous = commentsObj.isAnonymous || false;

                    // Only show link for Docs feedback
                    if (featureType === 'Docs' && commentsObj.documentPath) {
                      link = commentsObj.documentPath;
                    }
                  }
                } catch (e) {
                  // If parsing fails, keep default
                }

                const userName = isAnonymous ? 'Anonymous' : response.userRef.replace(/^user:default\//, '');

                return {
                  ...response,
                  id: `response-${index}`,
                  status: response.status || 'pending', // Use status from database
                  contactPreference: response.consent === true ? 'Yes' : response.consent === false ? 'No' : 'Not specified',
                  originalUserRef: response.userRef, // Store original for deletion
                  userRef: userName, // Display name without prefix
                  featureType: featureType,
                  link: link,
                  resolvedBy: response.resolved_by ? response.resolved_by.replace(/^user:default\//, '') : undefined,
                  resolvedAt: response.resolved_at,
                } as DisplayFeedbackResponse;
              });

              setResponses(displayResponses);
              return; // Exit early if successful
            } catch (parseError) {
              // JSON parse failed, continue to fallback
            }
          } else {
            await response.text(); // Read response to avoid memory leaks
            // API error, continue to fallback
          }
        } catch (error) {
          // Direct API failed, continue to fallback
        }

        // Fallback: show no responses
        // All API calls failed, showing empty state
      } catch (error) {
        console.error('Failed to load feedback responses:', error);
        console.error('Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          entityRef
        });
      } finally {
        setLoading(false);
      }
    };

    loadResponses();
  }, [entityRef]);

  const handleStatusChange = (responseId: string, newStatus: string) => {
    setStatusChanges(prev => ({
      ...prev,
      [responseId]: newStatus,
    }));
  };

  const saveStatusChange = async (responseId: string) => {
    const newStatus = statusChanges[responseId];
    if (!newStatus) return;

    // If status is "resolved", show confirmation dialog
    if (newStatus === 'resolved') {
      setPendingResolveId(responseId);
      setConfirmDialogOpen(true);
      return;
    }

    try {
      // Find the feedback to update
      const feedbackToUpdate = responses.find(r => r.id === responseId);
      if (!feedbackToUpdate) {
        console.error('Feedback not found');
        return;
      }

      // Use originalUserRef for API call
      const userRefToUpdate = feedbackToUpdate.originalUserRef || feedbackToUpdate.userRef;

      // Call the backend API to update the status in database
      const baseUrl = await discoveryApi.getBaseUrl('feedback-direct');
      const { token } = await identityApi.getCredentials();
      const identity = await identityApi.getBackstageIdentity();
      const currentUser = identity?.userEntityRef || 'unknown';

      const updateUrl = `${baseUrl}/feedback/update-status`;

      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          entityRef: entityRef,
          userRef: userRefToUpdate,
          response: feedbackToUpdate.response,
          featureType: feedbackToUpdate.featureType !== 'Not specified' ? feedbackToUpdate.featureType : undefined,
          status: newStatus,
          resolvedBy: newStatus === 'resolved' ? currentUser : undefined
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update failed:', errorText);
        throw new Error(`Failed to update feedback status: ${errorText}`);
      }

      await response.json();

      // Update UI state immediately
      setResponses(prev => prev.map(resp =>
        resp.id === responseId
          ? {
              ...resp,
              status: newStatus as any,
            }
          : resp
      ));

      setStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[responseId];
        return updated;
      });

    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please check the console for details.');
    }
  };

  const handleConfirmResolve = async () => {
    if (!pendingResolveId) return;

    const newStatus = statusChanges[pendingResolveId];
    if (!newStatus || newStatus !== 'resolved') {
      setConfirmDialogOpen(false);
      setPendingResolveId(null);
      return;
    }

    try {
      // Find the feedback to update
      const feedbackToUpdate = responses.find(r => r.id === pendingResolveId);
      if (!feedbackToUpdate) {
        console.error('Feedback not found');
        return;
      }

      // Use originalUserRef for API call
      const userRefToUpdate = feedbackToUpdate.originalUserRef || feedbackToUpdate.userRef;

      // Get current user info
      const baseUrl = await discoveryApi.getBaseUrl('feedback-direct');
      const { token } = await identityApi.getCredentials();
      const identity = await identityApi.getBackstageIdentity();
      const currentUser = identity?.userEntityRef || 'unknown';
      const currentUserName = currentUser.replace(/^user:default\//, '');

      const updateUrl = `${baseUrl}/feedback/update-status`;

      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          entityRef: entityRef,
          userRef: userRefToUpdate,
          response: feedbackToUpdate.response,
          featureType: feedbackToUpdate.featureType !== 'Not specified' ? feedbackToUpdate.featureType : undefined,
          status: newStatus,
          resolvedBy: currentUser
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update failed:', errorText);
        throw new Error(`Failed to update feedback status: ${errorText}`);
      }

      await response.json();

      // Update UI state immediately with resolved info
      setResponses(prev => prev.map(resp =>
        resp.id === pendingResolveId
          ? {
              ...resp,
              status: 'resolved' as any,
              resolvedBy: currentUserName,
              resolvedAt: new Date().toISOString()
            }
          : resp
      ));

      setStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[pendingResolveId];
        return updated;
      });

    } catch (error) {
      console.error('Failed to resolve feedback:', error);
      alert('Failed to resolve feedback. Please check the console for details.');
    } finally {
      setConfirmDialogOpen(false);
      setPendingResolveId(null);
    }
  };

  const handleCancelResolve = () => {
    if (!pendingResolveId) {
      setConfirmDialogOpen(false);
      return;
    }

    // Clear the status change
    setStatusChanges(prev => {
      const updated = { ...prev };
      delete updated[pendingResolveId];
      return updated;
    });

    setConfirmDialogOpen(false);
    setPendingResolveId(null);
  };


  const getStatusChip = (status: string) => {
    const chipProps = {
      pending: { label: 'Pending', className: classes.chipPending },
      in_progress: { label: 'In Progress', className: classes.chipInProgress },
      resolved: { label: 'Resolved', className: classes.chipResolved },
    };

    const props = chipProps[status as keyof typeof chipProps] || chipProps.pending;
    return <Chip size="small" {...props} />;
  };


  if (loading) {
    return <Typography>Loading feedback responses...</Typography>;
  }

  if (responses.length === 0) {
    return (
      <Paper>
        <Box p={2}>
          <Typography variant="h6">Entity Feedback Responses</Typography>
          <Typography>No feedback responses yet.</Typography>
        </Box>
      </Paper>
    );
  }


  // Filter responses based on selected statuses
  const filteredResponses = responses.filter(response =>
    statusFilter.includes(response.status || 'pending')
  );

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  return (
    <>
      <Paper>
        <Box p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Entity Feedback Responses</Typography>
            <Box display="flex" style={{ gap: '8px' }}>
              <Chip
                label="Pending"
                color={statusFilter.includes('pending') ? 'primary' : 'default'}
                onClick={() => handleStatusFilterChange('pending')}
                size="small"
              />
              <Chip
                label="In Progress"
                color={statusFilter.includes('in_progress') ? 'primary' : 'default'}
                onClick={() => handleStatusFilterChange('in_progress')}
                size="small"
              />
              <Chip
                label="Resolved"
                color={statusFilter.includes('resolved') ? 'primary' : 'default'}
                onClick={() => handleStatusFilterChange('resolved')}
                size="small"
              />
            </Box>
          </Box>
          <TableContainer>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.userColumn}>User</TableCell>
                  <TableCell className={classes.featureTypeColumn}>Feature Type</TableCell>
                  <TableCell className={classes.linkColumn}>Source</TableCell>
                  <TableCell className={classes.contactColumn}>OK TO CONTACT?</TableCell>
                  <TableCell className={classes.commentColumn}>Feedback Response</TableCell>
                  <TableCell className={classes.statusColumn}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredResponses.map((response) => (
                  <TableRow key={response.id}>
                    <TableCell className={classes.userColumn} style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <Box className={classes.userCell}>
                        {response.userRef === 'Anonymous' ? (
                          <>
                            <Avatar className={classes.userAvatar}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Typography
                              variant="body2"
                              color="primary"
                              style={{ cursor: 'default' }}
                            >
                              {response.userRef}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Link
                              href={`/catalog/default/user/${response.userRef}`}
                              color="inherit"
                              underline="none"
                            >
                              <Avatar className={classes.userAvatar}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                            </Link>
                            <Link
                              href={`/catalog/default/user/${response.userRef}`}
                              color="primary"
                              underline="hover"
                            >
                              {response.userRef}
                            </Link>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.featureTypeColumn} style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <Typography
                        variant="body2"
                        color="textPrimary"
                      >
                        {response.featureType || 'Not specified'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.linkColumn} style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      {response.link && response.link !== '-' ? (
                        <Link
                          href={response.link}
                          color="primary"
                          underline="hover"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Typography
                            variant="body2"
                            style={{
                              wordWrap: 'break-word',
                            }}
                          >
                            {(() => {
                              // Extract page name from path (last segment)
                              const parts = response.link.split('/').filter(p => p);
                              const pageName = parts[parts.length - 1] || 'docs';
                              // Capitalize first letter and replace hyphens with spaces
                              return pageName.charAt(0).toUpperCase() + pageName.slice(1).replace(/-/g, ' ');
                            })()}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.contactColumn} style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <Typography
                        variant="body2"
                        color={response.contactPreference === 'Yes' ? 'primary' : 'textSecondary'}
                      >
                        {response.contactPreference}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.commentColumn} style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <Typography
                        variant="body2"
                        style={{
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {response.response || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.statusColumn} style={{ verticalAlign: 'top' }}>
                      {statusChanges[response.id || ''] ? (
                        <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
                          <FormControl className={classes.statusSelect} size="small" variant="outlined">
                            <Select
                              value={statusChanges[response.id || '']}
                              onChange={(e) => handleStatusChange(response.id || '', e.target.value as string)}
                            >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="in_progress">In Progress</MenuItem>
                              <MenuItem value="resolved">Resolved</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => saveStatusChange(response.id || '')}
                            title="Save status change"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          onClick={response.status !== 'resolved' ? () => handleStatusChange(response.id || '', response.status || 'pending') : undefined}
                          style={{ cursor: response.status !== 'resolved' ? 'pointer' : 'default' }}
                        >
                          {getStatusChip(response.status || 'pending')}
                          {response.status === 'resolved' && response.resolvedBy && (
                            <Typography variant="caption" display="block" style={{ marginTop: '4px', color: '#666', fontSize: '10px' }}>
                              Resolved by: {response.resolvedBy}
                              {response.resolvedAt && (
                                <span style={{ display: 'block' }}>
                                  {new Date(response.resolvedAt).toLocaleString()}
                                </span>
                              )}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelResolve}>
        <DialogTitle>Confirm Resolution</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure this feedback is resolved? Once marked as resolved, it cannot be changed back to pending or in progress.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelResolve} color="default">
            Cancel
          </Button>
          <Button onClick={handleConfirmResolve} color="primary" variant="contained">
            Yes, Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};